import { ApiError, Unauthenticated } from '../../../api.js';
import { viewFetch } from '../../../view-as.js';
import { withDeadline } from '../../deadline.js';
import type { Added, Home, Parked, SearchAnswer } from '../contract.js';

async function json<T>(response: Response): Promise<T> {
  if (response.status === 401) throw new Unauthenticated();
  if (!response.ok) {
    const body = await response.json().catch(() => ({})) as { error?: unknown };
    throw new ApiError(response.status, typeof body.error === 'string' ? body.error : `${response.status} ${response.statusText}`);
  }
  if (response.status === 204) return undefined as T;
  return await response.json() as T;
}
const q = encodeURIComponent;
export function getHome(store: string, signal?: AbortSignal): Promise<Home> {
  return withDeadline(`/api/admin/home?store=${q(store)}`, { credentials: 'same-origin' }, json<Home>, signal);
}
export function search(store: string, query: string, signal?: AbortSignal): Promise<SearchAnswer> {
  return withDeadline(`/api/admin/search?store=${q(store)}&q=${q(query)}`, { credentials: 'same-origin' }, json<SearchAnswer>, signal);
}
/** Existing non-parking admin writes use `done` for their saved sentence. */
export type HomeWriteResult = { done?: string; changeId?: string; takeBackUntil?: string; takeBackNote?: string } | undefined;
async function send<T>(store: string, path: string, body: Record<string, unknown> = {}): Promise<T> {
  const response = await viewFetch(path, { method: 'POST', credentials: 'same-origin',
    headers: { 'content-type': 'application/json' }, body: JSON.stringify({ store, ...body }) });
  return json<T>(response);
}
export type ParkUntil = 'tomorrow' | 'monday' | 'week';
export function park(store: string, key: string, until: ParkUntil): Promise<Parked> {
  return send<Parked>(store, `/api/admin/home/items/${q(key)}/park`, { until });
}
export function leave(store: string, key: string): Promise<Parked> { return send<Parked>(store, `/api/admin/home/items/${q(key)}/leave`); }
export async function invite(store: string, path: string, reason: string): Promise<Added> {
  const response = await viewFetch(path, { method: 'POST', credentials: 'same-origin',
    headers: { 'content-type': 'application/json' }, body: JSON.stringify({ store, send: true, reason }) });
  if (response.status === 401) throw new Unauthenticated();
  const body: unknown = await response.json().catch(() => null);
  if (body && typeof body === 'object') {
    if ('principalId' in body && 'invited' in body) return body as Added;
    if ('error' in body && typeof body.error === 'string') throw new ApiError(response.status, body.error);
  }
  throw new ApiError(response.status, `${response.status} ${response.statusText}`);
}
export async function post(store: string, path: string, reason: string): Promise<HomeWriteResult> {
  const result = await send<unknown>(store, path, { reason });
  if (!result || typeof result !== 'object' || !('done' in result) || typeof result.done !== 'string') return undefined;
  // A change that can be taken back says so; Home's saved card then offers Take it back, as drawing 01 does.
  const { changeId, takeBackUntil, takeBackNote } = result as { changeId?: unknown; takeBackUntil?: unknown; takeBackNote?: unknown };
  return { done: result.done, ...(typeof changeId === 'string' && typeof takeBackUntil === 'string' ? { changeId, takeBackUntil } : {}),
    ...(typeof takeBackNote === 'string' ? { takeBackNote } : {}) };
}
